import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Container, Form, Button, Row, Col, Card, Image, Modal } from 'react-bootstrap';
import { FaEnvelope, FaUser, FaPhone, FaLock, FaCheck } from "react-icons/fa";
import Layout from "../../../layout/fullpage";
import { Logo, Media, MediaGroup } from "../../../components";
import Select from "react-select";
import { FaBriefcase } from "react-icons/fa";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const Register = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [customerData, setCustomerData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        password: '',
        confirmPassword: '',
        parent_id: id || '',
        leg: "0",
        verification_otp: ''
    });
    const [showPasswords, setShowPasswords] = useState(false); // Single state for both fields
    const [professionOptions, setProfessionOptions] = useState([]);
    const [selectedProfession, setSelectedProfession] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedProfessionId, setSelectedProfessionId] = useState(null);
    const [loadingProfessions, setLoadingProfessions] = useState(false);
    const [errors, setErrors] = useState({});
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otp, setOtp] = useState('');
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);
    const [verificationData, setVerificationData] = useState({
        phone_number: '',
        otp: ''
    });
    const [otpSending, setOtpSending] = useState(false);
    const [otpVerifying, setOtpVerifying] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const legFromUrl = urlParams.get('Team');
        if (legFromUrl) {
            setCustomerData(prevData => ({
                ...prevData,
                leg: legFromUrl,
            }));
        }
    }, []);

    useEffect(() => {
        const fetchProfessions = async () => {
            setLoadingProfessions(true);
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_HOST}api/professions`
                );
                const options = response.data.map((profession) => ({
                    value: profession.id,
                    label: profession.name,
                }));
                setProfessionOptions(options);
            } catch (error) {
                console.error("Error fetching professions:", error);
                setProfessionOptions([]);
            } finally {
                setLoadingProfessions(false);
            }
        };

        fetchProfessions();
    }, []);

    const validate = (name, value) => {
        const newErrors = { ...errors };

        if (name === 'phone_number') {
            if (!/^\d{10}$/.test(value)) {
                newErrors.phone_number = 'Primary phone number must be exactly 10 digits.';
            } else {
                delete newErrors.phone_number;
            }
        }

        if (name === 'password') {
            if (value.length < 6) {
                newErrors.password = 'Password must be at least 6 characters long.';
            } else {
                delete newErrors.password;
            }
        }

        if (name === 'confirmPassword') {
            if (value !== customerData.password) {
                newErrors.confirmPassword = 'Password and Confirm Password must match.';
            } else {
                delete newErrors.confirmPassword;
            }
        }

        setErrors(newErrors);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCustomerData({ ...customerData, [name]: value });
        validate(name, value);
    };

    const handleSendOtp = async () => {
        if (!customerData.phone_number || customerData.phone_number.length !== 10 || errors.phone_number) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please enter a valid 10-digit phone number first',
            });
            return;
        }

        try {
            setOtpSending(true);
            const response = await axios.post(`${process.env.REACT_APP_HOST}api/phone-verification/send-otp`, {
                phone_number: customerData.phone_number
            });

            if (response.data.success) {
                setShowOtpModal(true);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.data.message || 'Failed to send OTP',
                });
            }
        } catch (err) {
            let errorMessage = 'Failed to verify phone number';
            if (err.response && err.response.data) {
                errorMessage = err.response.data.message || errorMessage;
            }
            Swal.fire({
                imageUrl: "/shop/images/Failed_1.svg",
                imageWidth: '100%',
                imageHeight: 'auto',
                imageAlt: "failed",
                text: errorMessage,
            });
        } finally {
            setOtpSending(false);
        }
    };

    const verifyOtp = async () => {
        if (!otp || otp.length !== 6) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please enter a valid 6-digit OTP',
            });
            return;
        }

        try {
            setOtpVerifying(true);
            const response = await axios.post(`${process.env.REACT_APP_HOST}api/phone-verification/verify-otp`, {
                phone_number: customerData.phone_number,
                otp: otp
            });

            if (response.data.success) {
                // ✅ Show success Swal with image and auto-close
                Swal.fire({
                    imageUrl: "/shop/images/phone-verified.svg",
                    imageWidth: '100%',
                    imageHeight: 'auto',
                    imageAlt: "Success Image",
                    title: 'Mobile Verified',
                    text: response.data.message || 'Your phone number has been verified successfully.',
                    timer: 3000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                });
                setIsPhoneVerified(true);
                setVerificationData({
                    phone_number: customerData.phone_number,
                    otp: otp
                });
                setCustomerData(prev => ({
                    ...prev,
                    verification_otp: otp
                }));
                setShowOtpModal(false);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: response.data.message || 'Invalid OTP',
                });
            }
        } catch (err) {
            let errorMessage = 'Failed to verify OTP';
            if (err.response && err.response.data) {
                errorMessage = err.response.data.message || errorMessage;
            }
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: errorMessage,
            });
        } finally {
            setOtpVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!isPhoneVerified || verificationData.phone_number !== customerData.phone_number) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please verify your phone number first',
            });
            setLoading(false);
            return;
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_HOST}api/register`, {
                first_name: customerData.first_name,
                last_name: customerData.last_name,
                phone_number: customerData.phone_number,
                password: customerData.password,
                c_password: customerData.confirmPassword,
                parent_id: customerData.parent_id,
                leg: customerData.leg,
                verification_otp: customerData.verification_otp,
                professional_id: selectedProfessionId,
            });

            if (customerData.email) {
                response.email = customerData.email;
            }

            if (response.status === 201) {
                localStorage.setItem('authToken', response.data.token);
                localStorage.setItem('user_type', response.data.user_type || 'distributor');
                localStorage.setItem('user_name', response.data.data.name);
                localStorage.setItem('user_phone', response.data.data.phone);
                localStorage.setItem('user_id', response.data.data.id);

                Swal.fire({
                    imageUrl: "/shop/images/Welcome.svg",
                    imageWidth: '100%',
                    imageHeight: 'auto',
                    imageAlt: "Welcome",
                    title: "Welcome to Elite World",
                    text: "Welcome to Elite World",
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: false,
                    customClass: {
                        image: 'full-size-image'
                    }
                });

                setTimeout(() => {
                    navigate('/shop');
                }, 2000);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Unexpected response status: ' + response.status,
                });
            }
        } catch (err) {
            let errorMessage = 'An error occurred during registration.';
            let errorTitle = 'Error';

            if (err.response?.data) {
                const responseData = err.response.data;

                // Handle duplicate email/phone cases
                if (responseData.message === 'Duplicate email found. Please use another email.') {
                    errorMessage = 'The email address is already registered. Please use a different email.';
                } else if (responseData.message === 'Duplicate phone number found. Please use another phone number.') {
                    debugger;
                    errorMessage = 'The mobile number is already registered. Please use a different mobile number.';
                }
                // Handle validation errors
                else if (responseData.errors) {
                    if (typeof responseData.errors === 'object') {
                        // For field-specific validation errors
                        errorMessage = Object.values(responseData.errors).flat().join(' ');
                    } else {
                        // For other error messages
                        errorMessage = responseData.errors;
                    }
                }
                // Handle OTP verification failure
                else if (responseData.message && responseData.success === false) {
                    errorMessage = responseData.message;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            Swal.fire({
                icon: 'none',
                imageUrl: "/shop/images/Failed_1.svg",
                imageWidth: '100%',
                imageHeight: 'auto',
                imageAlt: "Error",
                text: errorMessage,
                customClass: {
                    image: 'full-size-image'
                }
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout title="Register With Referral" centered>
            <div className="container p-2 p-sm-6">
                <Card className="overflow-hidden card-gutter-lg rounded-4 card-auth card-auth-mh">
                    <Row className="g-0 flex-column-reverse flex-lg-row">
                        <Col lg="4" className="order-lg-2">
                            <Card.Body className="h-100 d-flex flex-column justify-content-center">
                                <div className="nk-block-head text-center">
                                    <h3 className="nk-block-title mb-1">Create Your Account</h3>
                                    <Form onSubmit={handleSubmit}>
                                        <Form.Group className="mb-3 position-relative">
                                            <Form.Label className="fw-semibold text-start">First Name</Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type="text"
                                                    name="first_name"
                                                    value={customerData.first_name}
                                                    onChange={handleChange}
                                                    placeholder="Enter first name"
                                                    className="rounded-pill ps-5"
                                                    required
                                                />
                                                <FaUser className="position-absolute top-50 start-0 translate-middle-y ms-3" />
                                            </div>
                                        </Form.Group>

                                        <Form.Group className="mb-3 position-relative">
                                            <Form.Label className="fw-semibold text-start">Last Name</Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type="text"
                                                    name="last_name"
                                                    value={customerData.last_name}
                                                    onChange={handleChange}
                                                    placeholder="Enter last name"
                                                    className="rounded-pill ps-5"
                                                    required
                                                />
                                                <FaUser className="position-absolute top-50 start-0 translate-middle-y ms-3" />
                                            </div>
                                        </Form.Group>
                                        <Form.Group className="mb-3 position-relative">
                                            <Form.Label className="fw-semibold text-start">Email (Optional)</Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={customerData.email}
                                                    onChange={handleChange}
                                                    placeholder="Enter email (optional)"
                                                    isInvalid={!!errors.email}
                                                    className="rounded-pill ps-5"
                                                />
                                                <FaEnvelope className="position-absolute top-50 start-0 translate-middle-y ms-3" />
                                            </div>
                                            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3 position-relative">
                                            <Form.Label className="fw-semibold text-start">Mobile Number</Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type="tel"
                                                    name="phone_number"
                                                    value={customerData.phone_number}
                                                    onChange={handleChange}
                                                    placeholder="Enter mobile number"
                                                    isInvalid={!!errors.phone_number}
                                                    className="rounded-pill ps-5 pe-5"
                                                    required
                                                    disabled={isPhoneVerified}
                                                />
                                                <FaPhone className="position-absolute top-50 start-0 translate-middle-y ms-3" />

                                                {isPhoneVerified ? (
                                                    <FaCheck className="position-absolute top-50 end-0 translate-middle-y me-3 text-success" />
                                                ) : (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        className="position-absolute top-50 end-0 translate-middle-y me-2"
                                                        onClick={handleSendOtp}
                                                        disabled={otpSending || !!errors.phone_number}
                                                    >
                                                        {otpSending ? 'Sending...' : 'Verify'}
                                                    </Button>
                                                )}
                                            </div>
                                            <Form.Control.Feedback type="invalid">{errors.phone_number}</Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-3 position-relative">
                                            <Form.Label className="fw-semibold text-start">
                                                Profession
                                            </Form.Label>
                                            <div className="position-relative">
                                                <Select
                                                    options={professionOptions}
                                                    value={selectedProfession}
                                                    onChange={(selectedOption) => {
                                                        setSelectedProfession(selectedOption);
                                                        setSelectedProfessionId(selectedOption?.value || null);
                                                    }}
                                                    isLoading={loadingProfessions}
                                                    isSearchable
                                                    placeholder="Select profession"
                                                    className="rounded-pill"
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        control: (base) => ({
                                                            ...base,
                                                            borderRadius: "50px",
                                                            paddingLeft: "2.5rem",
                                                            borderColor: "#ced4da",
                                                        }),
                                                        indicatorSeparator: () => null,
                                                    }}
                                                />
                                                <FaBriefcase className="position-absolute top-50 start-0 translate-middle-y ms-3" />
                                            </div>
                                        </Form.Group>

                                        <Form.Group className="mb-3 position-relative">
                                            <Form.Label className="fw-semibold text-start">
                                                Password
                                            </Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type={showPasswords ? "text" : "password"}
                                                    name="password"
                                                    value={customerData.password}
                                                    onChange={handleChange}
                                                    placeholder="Enter password"
                                                    isInvalid={!!errors.password}
                                                    className="rounded-pill ps-5"
                                                    required
                                                />
                                                <FaLock className="position-absolute top-50 start-0 translate-middle-y ms-3" />
                                                {/* Single eye icon (only shown on the first password field) */}
                                                <div
                                                    className="position-absolute top-50 end-0 translate-middle-y me-3"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setShowPasswords(!showPasswords)}
                                                >
                                                    {showPasswords ? <FaEyeSlash /> : <FaEye />}
                                                </div>
                                            </div>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.password}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-4 position-relative">
                                            <Form.Label className="fw-semibold text-start">
                                                Confirm Password
                                            </Form.Label>
                                            <div className="position-relative">
                                                <Form.Control
                                                    type={showPasswords ? "text" : "password"} // Same toggle state
                                                    name="confirmPassword"
                                                    value={customerData.confirmPassword}
                                                    onChange={handleChange}
                                                    placeholder="Confirm password"
                                                    isInvalid={!!errors.confirmPassword}
                                                    className="rounded-pill ps-5"
                                                    required
                                                />
                                                <FaLock className="position-absolute top-50 start-0 translate-middle-y ms-3" />
                                            </div>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.confirmPassword}
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <div className="d-flex justify-content-center">
                                            <Form.Group className="mb-4">
                                                <Form.Check
                                                    type="checkbox"
                                                    id="terms-checkbox"
                                                    label={
                                                        <span>
                                                            I agree to the{" "}
                                                            <a
                                                                href="#"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    setShowTermsModal(true);
                                                                }}
                                                                className="text-primary"
                                                            >
                                                                Terms and Conditions
                                                            </a>
                                                        </span>
                                                    }
                                                    checked={termsAccepted}
                                                    onChange={(e) => setTermsAccepted(e.target.checked)}
                                                />
                                            </Form.Group>
                                        </div>

                                        <Button
                                            type="submit"
                                            className="w-100 rounded-pill py-2 fw-bold"
                                            variant="primary"
                                            disabled={loading || Object.keys(errors).length > 0 || !isPhoneVerified || !termsAccepted}
                                        >
                                            {loading ? 'Registering...' : 'Register'}
                                        </Button>
                                    </Form>

                                    <div className="mt-4 text-center">
                                        <Link to="/shop/auths/login" className="text-decoration-none text-secondary">
                                            Already have an account? <strong className="text-primary">Login Here</strong>
                                        </Link>
                                    </div>
                                </div>
                            </Card.Body>
                        </Col>
                        <Col lg="8" className="d-block p-0 mobile-image-container">
                            <Card.Body
                                className="h-100 d-flex flex-column justify-content-center p-0"
                                style={{
                                    backgroundImage: "url('/shop/images/Resgistor2.svg')",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                    minHeight: "52vh" // 35% of viewport height on mobile
                                }}
                            />
                        </Col>
                    </Row>
                </Card>
            </div>

            {/* OTP Verification Modal */}
            <Modal show={showOtpModal} onHide={() => setShowOtpModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Verify Phone Number</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center my-3">
                        <img
                            src="/shop/images/OTP.svg" // replace with your actual image path
                            alt="OTP Verification"
                        />
                    </div>
                    <p>We've sent a 6-digit OTP to your phone number {customerData.phone_number}</p>
                    <Form.Group>
                        <Form.Label>Enter OTP</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter 6-digit OTP"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            maxLength={6}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowOtpModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={verifyOtp} disabled={otpVerifying || otp.length !== 6}>
                        {otpVerifying ? 'Verifying...' : 'Verify'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Terms and Conditions Modal */}
            <Modal show={showTermsModal} onHide={() => setShowTermsModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Terms and Conditions</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div class="container">
                        <div class="row mb-4">
                            <div class="col-12">
                                <h2 class="section__title">EliteWorld Terms and Conditions</h2>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-12">
                                <p><strong>Please read these Terms and Conditions carefully before using our platform. By using our
                                    platform, you agree to be bound by these Terms and Conditions.</strong></p>

                                <h5>Definitions</h5>
                                <ul>
                                    <li>"We" or "us" refers to EliteWorld.</li>
                                    <li>"You" refers to the user of our platform.</li>
                                    <li>"Platform" refers to our website, mobile app, or any other digital platform we may use.</li>
                                </ul>

                                <h5>Use of the Platform</h5>
                                <ul>
                                    <li>You must be at least 18 years old to use our platform.</li>
                                    <li>You must provide accurate and complete information when creating an account or using our platform.
                                    </li>
                                    <li>You are responsible for maintaining the confidentiality of your account and password.</li>
                                    <li>You agree to use our platform only for lawful purposes and in compliance with all applicable laws and
                                        regulations.</li>
                                </ul>

                                <h5>Products and Services</h5>
                                <ul>
                                    <li>We offer various products and services on our platform, including but not limited to, online shopping,
                                        delivery services, and customer support.</li>
                                    <li>We reserve the right to modify or discontinue any product or service at any time without notice.</li>
                                </ul>

                                <h5>Payment Terms</h5>
                                <ul>
                                    <li>All payments must be made in the currency specified on our platform.</li>
                                    <li>We accept various payment methods, including but not limited to, credit cards, debit cards, and online
                                        payment services.</li>
                                    <li>You agree to pay all applicable taxes and fees associated with your use of our platform.</li>
                                </ul>

                                <h5>Intellectual Property</h5>
                                <ul>
                                    <li>All content on our platform, including but not limited to, text, images, and logos, is owned by us or
                                        our licensors.</li>
                                    <li>You agree not to reproduce, modify, or distribute any content on our platform without our prior
                                        written consent.</li>
                                </ul>

                                <h5>Disclaimer of Warranties</h5>
                                <p>Our platform is provided on an "as-is" and "as-available" basis. We disclaim all warranties, express or
                                    implied, including but not limited to, implied warranties of merchantability, fitness for a particular
                                    purpose, and non-infringement.</p>

                                <h5>Limitation of Liability</h5>
                                <p>We will not be liable for any damages, including but not limited to, incidental, consequential, or
                                    punitive damages, arising out of or related to your use of our platform. Our liability for damages will be
                                    limited to the amount of fees you have paid to us.</p>

                                <h5>Termination</h5>
                                <p>We reserve the right to terminate or suspend your account and access to our platform at any time without
                                    notice. Upon termination, you will no longer have access to our platform, and we may delete any content or
                                    data associated with your account.</p>

                                <h5>Governing Law</h5>
                                <p>These Terms and Conditions will be governed by and construed in accordance with the laws of Hubballi
                                    Jurisdiction. Any disputes arising out of or related to these Terms and Conditions will be resolved
                                    through [insert dispute resolution process].</p>

                                <h5>Changes to Terms and Conditions</h5>
                                <p>We reserve the right to modify or update these Terms and Conditions at any time without notice. Your
                                    continued use of our platform will be deemed acceptance of any changes to these Terms and Conditions.</p>

                                <h5>Contact Us</h5>
                                <p>If you have any questions or concerns about these Terms and Conditions, please contact us at <a
                                    href="mailto:support@eliteworldpro.com">support@eliteworldpro.com</a>.</p>

                                <p><strong>By using our platform, you acknowledge that you have read, understood, and agreed to be bound by
                                    these Terms and Conditions.</strong></p>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </Layout>
    );
};

export default Register;